class HeaderMegaMenuV2 extends HTMLElement {
  constructor() {
    super();
    this.closeTimer = null;
    this.closeDelay = 150;
    this.desktopMql = window.matchMedia('(min-width: 990px)');
    this.productsCache = new Map();
    this.productsSectionId = 'header-mega-menu-products';
    this.productsHoverLocked = false;
    this.subLinkActivationTimer = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
  }

  isDesktop() {
    return this.desktopMql.matches;
  }

  connectedCallback() {
    this.navItems = this.querySelectorAll('.header-v2__nav-item--has-mega');

    this.navItems.forEach((item) => {
      const navLink = item.querySelector('.header-v2__nav-link');
      const mega = item.querySelector('.header-v2__mega');

      item.addEventListener('mouseenter', () => {
        if (!this.isDesktop()) return;
        this.openMegaMenu(item);
      });
      item.addEventListener('focusin', (event) => {
        if (event.target.closest('.header-v2__mega')) return;
        this.openMegaMenu(item);
      });
      item.addEventListener('mouseleave', (event) => this.handleNavItemMouseLeave(event, item, mega));

      if (mega) {
        mega.addEventListener('mouseenter', () => {
          if (!this.isDesktop()) return;
          this.cancelClose();
        });
        mega.addEventListener('mouseleave', (event) => this.handleMegaMouseLeave(event, item));
      }

      if (navLink) {
        navLink.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.toggleMegaMenu(item);
          }

          if (event.key === 'Escape') {
            this.closeAll();
          }
        });
      }

      this.bindPrimaryLinks(item);
      this.bindSubLinks(item);
      this.bindProductsPanel(item);
      this.bindProductsGridWheel(item);
      this.bindProductsScrollbar(item);
    });

    window.addEventListener('resize', () => {
      this.navItems.forEach((item) => this.updateProductsScrollbar(item.querySelector('.header-v2__products-wrap')));
    });

    this.addEventListener('mouseleave', (event) => {
      if (!this.isDesktop()) return;
      if (this.isWithinMenuArea(event.relatedTarget)) return;
      this.scheduleClose();
    });

    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.closeAll();
      }
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    this.cancelClose();
    this.cancelSubLinkActivation();
  }

  hasBestsellers(navItem) {
    return Boolean(navItem.querySelector('.header-v2__bestsellers-wrap'));
  }

  isEsMarket() {
    return Boolean(this.closest('.header-v2--es'));
  }

  shouldKeepBestsellers(navItem) {
    return this.isEsMarket() && this.hasBestsellers(navItem);
  }

  getDefaultRightColumnMode(navItem) {
    return this.hasBestsellers(navItem) ? 'bestsellers' : 'promo';
  }

  bindPrimaryLinks(navItem) {
    const primaryLinks = navItem.querySelectorAll('.header-v2__primary-link');
    const footer = navItem.querySelector('.header-v2__mega-footer');
    const subPanels = navItem.querySelectorAll('.header-v2__sub-panel');
    const megaGrid = navItem.querySelector('.header-v2__mega-grid');

    primaryLinks.forEach((link) => {
      link.addEventListener('mouseenter', () => {
        this.cancelClose();

        const wasActive = link.classList.contains('is-active');
        const productsWrap = navItem.querySelector('.header-v2__products-wrap');
        const productsVisible = productsWrap && !productsWrap.hidden;

        primaryLinks.forEach((item) => item.classList.remove('is-active'));
        link.classList.add('is-active');

        const panelId = link.dataset.panelId;
        const hasChildren = link.dataset.hasChildren === 'true';
        let activePanel = null;

        subPanels.forEach((panel) => {
          const isActive = panel.dataset.panelId === panelId;
          panel.hidden = !isActive;
          panel.classList.toggle('is-active', isActive);
          if (isActive) activePanel = panel;
        });

        if (footer) {
          footer.classList.toggle('is-hidden', hasChildren);
        }

        if (activePanel?.dataset.panelMode === 'products') {
          if (this.shouldKeepBestsellers(navItem)) {
            this.setRightColumnMode(navItem, 'bestsellers');
            this.clearSubLinks(navItem);
          } else if (!(wasActive && productsVisible)) {
            this.setRightColumnMode(navItem, this.hasBestsellers(navItem) ? 'bestsellers' : 'none');
            this.clearSubLinks(navItem);
          }
        } else {
          this.setRightColumnMode(navItem, this.getDefaultRightColumnMode(navItem));
          this.clearSubLinks(navItem);
        }

        if (megaGrid && activePanel?.dataset.panelMode !== 'products') {
          megaGrid.classList.remove('header-v2__mega-grid--products');
        }
      });

      link.addEventListener('focus', () => link.dispatchEvent(new Event('mouseenter', { bubbles: true })));
    });
  }

  bindSubLinks(navItem) {
    navItem.querySelectorAll('[data-products-url]').forEach((link) => {
      link.addEventListener('mouseenter', (event) => {
        if (!this.isDesktop()) return;
        if (this.shouldIgnoreSubLinkActivation(event)) return;

        this.cancelClose();
        this.scheduleSubLinkActivation(navItem, link);
      });

      link.addEventListener('mouseleave', (event) => {
        if (event.relatedTarget?.closest?.('.header-v2__products-wrap')) return;
        this.cancelSubLinkActivation();
      });

      link.addEventListener('focus', () => link.dispatchEvent(new Event('mouseenter', { bubbles: true })));
    });
  }

  bindProductsPanel(navItem) {
    navItem.querySelectorAll('.header-v2__products-wrap, .header-v2__bestsellers-wrap').forEach((panel) => {
      if (panel.dataset.interactionBound === 'true') return;

      panel.dataset.interactionBound = 'true';

      if (panel.classList.contains('header-v2__products-wrap')) {
        panel.addEventListener('mouseenter', () => {
          this.productsHoverLocked = true;
          this.cancelSubLinkActivation();
          this.cancelClose();
        });

        panel.addEventListener('mouseleave', (event) => {
          if (event.relatedTarget?.closest?.('.header-v2__products-wrap')) return;
          this.productsHoverLocked = false;
        });
      }

      panel.addEventListener(
        'mousedown',
        (event) => {
          if (event.button !== 0) return;

          const card = event.target.closest('a.header-v2__product-card');
          if (!card?.href) return;

          event.preventDefault();
          event.stopPropagation();
          window.location.assign(card.href);
        },
        true
      );
    });
  }

  shouldIgnoreSubLinkActivation(event) {
    if (this.productsHoverLocked) return true;
    return Boolean(event.relatedTarget?.closest?.('.header-v2__products-wrap'));
  }

  scheduleSubLinkActivation(navItem, link) {
    this.cancelSubLinkActivation();

    this.subLinkActivationTimer = window.setTimeout(() => {
      if (this.shouldIgnoreSubLinkActivation({ relatedTarget: null })) return;
      this.activateSubLink(navItem, link);
    }, 120);
  }

  cancelSubLinkActivation() {
    if (!this.subLinkActivationTimer) return;

    window.clearTimeout(this.subLinkActivationTimer);
    this.subLinkActivationTimer = null;
  }

  bindProductsGridWheel(navItem) {
    const productsWrap = navItem.querySelector('.header-v2__products-wrap');
    if (!productsWrap || productsWrap.dataset.wheelBound === 'true') return;

    productsWrap.dataset.wheelBound = 'true';
    productsWrap.addEventListener('wheel', (event) => this.handleProductsGridWheel(event), { passive: false });
  }

  handleProductsGridWheel(event) {
    if (!this.isDesktop()) return;

    const productsWrap = event.currentTarget;
    if (productsWrap.hidden) return;

    const grid =
      event.target.closest('.header-v2__products-grid') ||
      productsWrap.querySelector('.header-v2__products-grid');
    if (!grid) return;

    const maxScroll = grid.scrollHeight - grid.clientHeight;
    if (maxScroll <= 0) return;

    this.cancelClose();

    const deltaY = event.deltaY;
    const nextScrollTop = Math.min(maxScroll, Math.max(0, grid.scrollTop + deltaY));

    event.preventDefault();
    event.stopPropagation();

    if (nextScrollTop !== grid.scrollTop) {
      grid.scrollTop = nextScrollTop;
    }

    this.updateProductsScrollbar(productsWrap);
  }

  bindProductsScrollbar(navItem) {
    const productsWrap = navItem.querySelector('.header-v2__products-wrap');
    if (!productsWrap || productsWrap.dataset.scrollbarBound === 'true') return;

    productsWrap.dataset.scrollbarBound = 'true';
    productsWrap.addEventListener(
      'scroll',
      (event) => {
        if (event.target.classList.contains('header-v2__products-grid')) {
          this.updateProductsScrollbar(productsWrap);
        }
      },
      true
    );
  }

  updateProductsScrollbar(productsWrap) {
    if (!this.isDesktop() || !productsWrap || productsWrap.hidden) return;

    const grid = productsWrap.querySelector('.header-v2__products-grid');
    const scrollbar = productsWrap.querySelector('.header-v2__products-scrollbar');
    const thumb = productsWrap.querySelector('.header-v2__products-scrollbar-thumb');
    if (!grid || !scrollbar || !thumb) return;

    const maxScroll = grid.scrollHeight - grid.clientHeight;
    const isScrollable = maxScroll > 1;

    scrollbar.hidden = !isScrollable;
    if (!isScrollable) return;

    const trackHeight = scrollbar.clientHeight;
    const thumbHeight = Math.max(24, (grid.clientHeight / grid.scrollHeight) * trackHeight);
    const maxThumbTop = Math.max(0, trackHeight - thumbHeight);
    const thumbTop = maxScroll > 0 ? (grid.scrollTop / maxScroll) * maxThumbTop : 0;

    thumb.style.height = `${thumbHeight}px`;
    thumb.style.transform = `translateY(${thumbTop}px)`;
  }

  activateSubLink(navItem, link) {
    const panel = link.closest('.header-v2__sub-panel');
    if (!panel || panel.dataset.panelMode !== 'products') return;

    panel.querySelectorAll('.header-v2__sub-link').forEach((item) => item.classList.remove('is-active'));
    link.classList.add('is-active');

    // ES: keep bestsellers visible instead of swapping to category product grid
    if (this.shouldKeepBestsellers(navItem)) {
      this.setRightColumnMode(navItem, 'bestsellers');
      return;
    }

    this.setRightColumnMode(navItem, 'products');
    this.loadProducts(navItem, link.dataset.productsUrl);
  }

  setRightColumnMode(navItem, mode) {
    const promoWrap = navItem.querySelector('.header-v2__promo-wrap');
    const productsWrap = navItem.querySelector('.header-v2__products-wrap');
    const bestsellersWrap = navItem.querySelector('.header-v2__bestsellers-wrap');
    const megaGrid = navItem.querySelector('.header-v2__mega-grid');
    const showProducts = mode === 'products';
    const showBestsellers = mode === 'bestsellers' && Boolean(bestsellersWrap);
    const showPromo = mode === 'promo';

    if (promoWrap) promoWrap.hidden = !showPromo;
    if (productsWrap) productsWrap.hidden = !showProducts;
    if (bestsellersWrap) bestsellersWrap.hidden = !showBestsellers;
    if (megaGrid) megaGrid.classList.toggle('header-v2__mega-grid--products', showProducts);

    if (showBestsellers) {
      this.initRatingWidgets(bestsellersWrap);
    }

    if (showProducts) {
      requestAnimationFrame(() => this.updateProductsScrollbar(productsWrap));
    }
  }

  setProductsMode(navItem, enabled) {
    this.setRightColumnMode(navItem, enabled ? 'products' : this.getDefaultRightColumnMode(navItem));
  }

  clearSubLinks(navItem) {
    navItem.querySelectorAll('.header-v2__sub-link').forEach((link) => link.classList.remove('is-active'));
    const productsInner = navItem.querySelector('.header-v2__products-inner');
    if (productsInner) productsInner.innerHTML = '';
  }

  async loadProducts(navItem, productsUrl) {
    if (!productsUrl) return;

    const productsInner = navItem.querySelector('.header-v2__products-inner');
    if (!productsInner) return;

    if (this.productsCache.has(productsUrl)) {
      productsInner.innerHTML = this.productsCache.get(productsUrl);
      this.initRatingWidgets(productsInner);
      requestAnimationFrame(() => this.updateProductsScrollbar(navItem.querySelector('.header-v2__products-wrap')));
      return;
    }

    productsInner.innerHTML = '<div class="header-v2__products-loading" aria-hidden="true"></div>';

    let fetchPath = productsUrl;
    try {
      const parsedUrl = new URL(productsUrl, window.location.origin);
      fetchPath = `${parsedUrl.pathname}${parsedUrl.search}`;
    } catch (error) {
      fetchPath = productsUrl;
    }

    const separator = fetchPath.includes('?') ? '&' : '?';
    const fetchUrl = `${fetchPath}${separator}section_id=${this.productsSectionId}`;

    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) throw new Error('Failed to load products');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const content = doc.getElementById('header-mega-menu-products');

      const markup = content ? content.innerHTML : '';
      this.productsCache.set(productsUrl, markup);
      productsInner.innerHTML = markup;
      this.initRatingWidgets(productsInner);
      requestAnimationFrame(() => this.updateProductsScrollbar(navItem.querySelector('.header-v2__products-wrap')));
    } catch (error) {
      console.error('Header mega menu products:', error);
      productsInner.innerHTML = '';
    }
  }

  initRatingWidgets(container) {
    if (typeof window.vstarReload === 'function') {
      window.vstarReload();
      return;
    }

    document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: this.productsSectionId } }));
  }

  openMegaMenu(item) {
    this.cancelClose();
    const wasAlreadyActive = item.classList.contains('is-active');

    this.navItems.forEach((navItem) => {
      const isActive = navItem === item;
      navItem.classList.toggle('is-active', isActive);

      const mega = navItem.querySelector('.header-v2__mega');
      const navLink = navItem.querySelector('.header-v2__nav-link');

      if (mega) {
        mega.hidden = !isActive;
      }

      if (navLink) {
        navLink.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      }

      if (!isActive) {
        this.resetSubPanels(navItem);
      } else if (!wasAlreadyActive) {
        this.setRightColumnMode(navItem, this.getDefaultRightColumnMode(navItem));
      }
    });
  }

  toggleMegaMenu(item) {
    if (item.classList.contains('is-active')) {
      this.closeAll();
    } else {
      this.openMegaMenu(item);
    }
  }

  resetSubPanels(navItem) {
    navItem.querySelectorAll('.header-v2__primary-link').forEach((link) => link.classList.remove('is-active'));
    navItem.querySelectorAll('.header-v2__sub-panel').forEach((panel) => {
      panel.hidden = true;
      panel.classList.remove('is-active');
    });

    const footer = navItem.querySelector('.header-v2__mega-footer');
    if (footer) {
      footer.classList.remove('is-hidden');
    }

    this.setRightColumnMode(navItem, this.getDefaultRightColumnMode(navItem));
    this.clearSubLinks(navItem);

    const megaGrid = navItem.querySelector('.header-v2__mega-grid');
    if (megaGrid) {
      megaGrid.classList.remove('header-v2__mega-grid--products');
    }
  }

  cancelClose() {
    if (this.closeTimer) {
      window.clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }

  scheduleClose() {
    if (!this.isDesktop()) return;

    this.cancelClose();
    this.closeTimer = window.setTimeout(() => {
      this.closeAll();
    }, this.closeDelay);
  }

  isWithinMenuArea(target) {
    if (!target || !(target instanceof Node)) return false;
    if (this.contains(target)) return true;

    const openMega = this.querySelector('.header-v2__mega:not([hidden])');
    return Boolean(openMega?.contains(target));
  }

  handleNavItemMouseLeave(event, item, mega) {
    if (!this.isDesktop()) return;
    if (mega?.contains(event.relatedTarget)) return;
    if (this.isWithinMenuArea(event.relatedTarget)) return;
    this.scheduleClose();
  }

  handleMegaMouseLeave(event, item) {
    if (!this.isDesktop()) return;
    if (item.contains(event.relatedTarget)) return;
    if (this.isWithinMenuArea(event.relatedTarget)) return;
    this.scheduleClose();
  }

  closeAll() {
    this.cancelClose();
    this.cancelSubLinkActivation();
    this.productsHoverLocked = false;

    this.navItems.forEach((navItem) => {
      navItem.classList.remove('is-active');

      const mega = navItem.querySelector('.header-v2__mega');
      const navLink = navItem.querySelector('.header-v2__nav-link');

      if (mega) {
        mega.hidden = true;
      }

      if (navLink) {
        navLink.setAttribute('aria-expanded', 'false');
      }

      this.resetSubPanels(navItem);
    });
  }

  onDocumentClick(event) {
    const headerWrapper = this.closest('sticky-header') || this.closest('.header-wrapper');
    if (!headerWrapper?.contains(event.target)) {
      this.closeAll();
    }
  }
}

customElements.define('header-mega-menu-v2', HeaderMegaMenuV2);
